import { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Label } from "../../ui/label";
import { Switch } from "../../ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Badge } from "../../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import {
  Shield,
  Clock,
  Calendar,
  Search,
  FileText,
  CheckCircle2,
  Cloud,
  Database,
  ArrowLeft
} from "lucide-react";
import { DetailsDialog } from "../Cloud/DetailsDialog";
import { RequestCloudAccessDialogProps, Role } from "./interface";
import { myLocalStorage } from "../../StorageHelper";
import useUserStore from "../../../services/userStore";
import { submitForm } from "./Api/tenantgcpapi";
import { azureSubmitForm, submitK8Form } from "./Api/tenantazureapi";
import { awsSubmitForm } from "./Api/tenantawsapi";

// Kubernetes verbs
const k8sVerbs = [
  { id: "get", name: "Get", description: "Read individual resources" },
  { id: "list", name: "List", description: "List multiple resources" },
  { id: "watch", name: "Watch", description: "Watch for resource changes" },
  { id: "create", name: "Create", description: "Create new resources" },
  { id: "update", name: "Update", description: "Update existing resources" },
  { id: "patch", name: "Patch", description: "Partially update resources" },
  { id: "delete", name: "Delete", description: "Delete resources" },
  { id: "deletecollection", name: "Delete Collection", description: "Delete multiple resources" },
];

export default function RequestCloudAccessDialog({
  open,
  onOpenChange,
  resourceName,
  resourceType,
  resourceTypeLabel,
  availableRoles,
  resource,
  from,
  userId,
  userEmail,
  userName,
  upn,
  displayName,
  clustername,
  type
}: RequestCloudAccessDialogProps) {
  const [accessType, setAccessType] = useState("DYNAMIC");
  const [scheduleAccess, setScheduleAccess] = useState(false);
  const [justification, setJustification] = useState("");
  const [timeMinutes, setTimeMinutes] = useState("");
  const [selectedRoles, setSelectedRoles] = useState<Role[]>([]);
  const [step, setStep] = useState<"roleMode" | "roles" | "verbs" | "request">(resourceType === "kubernetes" ? "roleMode" : "roles");
  const [roleSearch, setRoleSearch] = useState("");
  const [verbSearch, setVerbSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [roleMode, setRoleMode] = useState<"existing" | "custom">("existing");
  const [selectedVerbs, setSelectedVerbs] = useState<string[]>([]);
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const userData = useUserStore((state) => state.user);
  const selectedAwsAccount = myLocalStorage.getItem("selectedAwsAccount");

  const toggleRoleSelect = (role: Role) => {
    setSelectedRoles(prev => {
      const isSelected = prev.some(r => r.id === role.id);
      return isSelected
        ? prev.filter(r => r.id !== role.id) // remove if already selected
        : [...prev, role]; // add if not selected
    });
  };

  const getStringBeforeAtSymbol = (str: any) => {
    const atIndex = str.indexOf("@");
    return atIndex !== -1 ? str.substring(0, atIndex) : str;
  };

  const handleSubmit = async () => {

    if (resourceType === "aws") {
      const matchedRoles = availableRoles?.filter(
        (r: Role) => selectedRoles.some(sr => sr.name === r.name)
      );
      const username = getStringBeforeAtSymbol(userData.email);
      console.log(userName)


      let payload = {
        userId: userData?.userId,
        username: username,
        awsUsername: userName,
        accessType: "resources",
        accessARN: resource.arn,
        accessName: resource.name,
        requestMessage: justification,
        requestStatus: "PENDING",
        privilegeLevel: accessType,
        selectedTime: accessType === "PERMANENT" ? 0 : timeMinutes,
        createdAt: new Date().toISOString(),
        tenantName: latestTenant,
        selectedPolicies: matchedRoles?.flatMap((r: any) => r.details || []) || [],
        type: "aws",
        timingMode: "GENERIC",
        scheduledStart: null,
        scheduledEnd: null,
        accountId: selectedAwsAccount,
      }
      await awsSubmitForm(payload);
    }
    else if (resourceType === "azure") {
      const allPathIds = selectedRoles.flatMap(role => role.path ?? []);
      let data = resource.details;

      let payload = {
        tenantName: latestTenant,
        subscriptionId: data?.subscriptionId,
        resourceScope:
          from === "vm"
            ? data?.instanceId
            : from === "storage"
              ? data?.azureStorageAccountId
              : from === "database"
                ? data?.instanceId
                : from === "kubernetes"
                  ? data?.azureId
                  : null,
        roleDefinitionPathIds: allPathIds,
        principleId: userId,
        principleType: "USER",
        expiryTimeAmount: timeMinutes,
        description: justification,
        userEmail: userData.email,
        timingMode: "",
        privilegeLevel: accessType.toUpperCase(),
      }
      await azureSubmitForm(payload);
    } else if (resourceType === "gcp") {
      let data = resource.details;
      const matchedRoles = availableRoles?.filter(
        (r: Role) => selectedRoles.some(sr => sr.name === r.name)
      );


      const roleDetails = matchedRoles?.flatMap((r: any) => r.details || []) || [];
      const payload = {
        projectId: data?.projectId || resource?.project || "",
        email: userData?.email || "",
        gcpUserEmail: userEmail || "",
        accessType: "resources",
        roles: roleDetails,
        requestMessage: justification,
        requestStatus: "PENDING",
        privilegeLevel: accessType.toUpperCase(),
        selectedTime: timeMinutes,
        tenantName: latestTenant,
        accessName: resource?.name || resourceName,
        resourceName: resourceName,
        gcpResourceType: resource?.type === "vm" ? "VIRTUAL_MACHINE" : resource?.type === "database" ? "DATABASE" : "STORAGE_BUCKET",
        timingMode: "",
      };
      await submitForm(payload);
    } else if (resourceType === "kubernetes") {
      let data = resource;
      const payload = roleMode === "custom"
        ? {
          verbs: selectedVerbs ? selectedVerbs : ["get"],
          resourcesName: data.name,
          k8ResourceType: type,
          k8ResourceId: data.uid,
          k8ResourceName: data.name,
          namespace: data.namespace,
          userName: upn,
          expiryTimeAmount: timeMinutes,
          wsUserEmail: userData.email,
          message: justification,
          clusterId: data.clusterId,
          cloudResourceAccountId: data.cloudResourceAccountId,
          cloudType: data.cloudProviderType,
          wsTenantName: latestTenant,
          user: displayName,
          clusterName: clustername,
          timingMode: "",
          privilegeLevel: accessType.toUpperCase(),
        }
        : {
          roleId: selectedRoles[0]?.roleUid,
          roleName: selectedRoles[0]?.roleName,
          roleKind: selectedRoles[0]?.roleKind,
          resourcesName: data.name,
          k8ResourceType: type,
          k8ResourceId: data.uid,
          k8ResourceName: data.name,
          namespace: data.namespace,
          userName: upn,
          expiryTimeAmount: timeMinutes,
          wsUserEmail: userData.email,
          message: justification,
          clusterId: data.clusterId,
          cloudResourceAccountId: data.cloudResourceAccountId,
          cloudType: data.cloudProviderType,
          wsTenantName: latestTenant,
          user: displayName,
          clusterName: clustername,
          timingMode: "",
          privilegeLevel: accessType.toUpperCase(),
        };

      console.log(payload)
      await submitK8Form(payload)
    } else if (resourceType === "aws") {

      const username = getStringBeforeAtSymbol(userData.email);

      const requestdata = {
        userId: userData?.userId,
        username: username,
        awsUsername: userName,
        accessType: from,
        accessARN: resource.arn,
        accessName: resourceName,
        requestMessage: justification,
        requestStatus: "PENDING",
        privilegeLevel: accessType,
        selectedTime:
          accessType === "PERMANENT" ? 0 : timeMinutes,
        createdAt: new Date().toISOString(),
        tenantName: latestTenant,
        selectedPolicies: selectedRoles,
        type: "aws",
        timingMode: "GENERIC",
        scheduledStart: null,
        scheduledEnd: null,
        accountId: selectedAwsAccount,
      };


    }


    onOpenChange(false);
  };

  const handleBack = () => {
    if (step === "request") {
      if (resourceType === "kubernetes" && roleMode === "custom") {
        setStep("verbs");
      } else {
        setStep(resourceType === "kubernetes" ? "roleMode" : "roles");
      }
    } else if (step === "verbs") {
      setStep("roleMode");
    } else if (step === "roles") {
      if (resourceType === "kubernetes") {
        setStep("roleMode");
      }
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    // Reset form
    setAccessType("DYNAMIC");
    setScheduleAccess(false);
    setJustification("");
    setTimeMinutes("");
    setSelectedRoles([]);
    setStep(resourceType === "kubernetes" ? "roleMode" : "roles");
    setRoleSearch("");
    setVerbSearch("");
    setStartDate("");
    setEndDate("");
    setRoleMode("existing");
    setSelectedVerbs([]);
  };

  // Reset form when dialog closes OR when resourceType changes
  useEffect(() => {
    if (!open) {
      setAccessType("DYNAMIC");
      setScheduleAccess(false);
      setJustification("");
      setTimeMinutes("");
      setSelectedRoles([]);
      setStep(resourceType === "kubernetes" ? "roleMode" : "roles");
      setRoleSearch("");
      setVerbSearch("");
      setStartDate("");
      setEndDate("");
      setRoleMode("existing");
      setSelectedVerbs([]);
    }
  }, [open]);

  // Reset when resourceType changes (switching between kubernetes/cloud resources)
  useEffect(() => {
    setStep(resourceType === "kubernetes" ? "roleMode" : "roles");
    setSelectedRoles([]);
    setRoleMode("existing");
    setSelectedVerbs([]);
  }, [resourceType]);


  // Filter roles based on search - use provided roles or fallback to mock
  const rolesToUse = availableRoles;
  const filteredRoles = rolesToUse?.filter(role =>
    role.name.toLowerCase().includes(roleSearch.toLowerCase()) ||
    (role.description && role.description.toLowerCase().includes(roleSearch.toLowerCase()))
  );

  // Filter verbs based on search
  const filteredVerbs = k8sVerbs.filter(verb =>
    verb.name.toLowerCase().includes(verbSearch.toLowerCase()) ||
    verb.description.toLowerCase().includes(verbSearch.toLowerCase())
  );

  const toggleVerb = (verbId: string) => {
    setSelectedVerbs(prev =>
      prev.includes(verbId)
        ? prev.filter(v => v !== verbId)
        : [...prev, verbId]
    );
  };

  // Step 1: Choose between existing role or custom role (Kubernetes only)
  if (step === "roleMode") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                <Database className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <DialogTitle className="text-xl font-semibold">Select Roles</DialogTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Choose one or more roles to request access to <span className="font-medium text-foreground">{resourceName}</span>
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            <Card>
              <CardContent className="pt-6 space-y-3">
                <div
                  className={`p-4 border-2 rounded-lg cursor-pointer hover-elevate transition-all ${roleMode === "existing" ? "border-blue-600 bg-blue-50 dark:bg-blue-950/20" : "border-muted"
                    }`}
                  onClick={() => setRoleMode("existing")}
                  data-testid="option-existing-role"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm">Select Existing Role</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Choose from pre-configured roles with predefined permissions
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className={`p-4 border-2 rounded-lg cursor-pointer hover-elevate transition-all ${roleMode === "custom" ? "border-blue-600 bg-blue-50 dark:bg-blue-950/20" : "border-muted"
                    }`}
                  onClick={() => setRoleMode("custom")}
                  data-testid="option-custom-role"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm">Use Custom Role</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Create a custom role by selecting specific Kubernetes verbs
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={handleCancel} data-testid="button-cancel" className="px-6">
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (roleMode === "existing") {
                    setStep("roles");
                  } else {
                    setStep("verbs");
                  }
                }}
                data-testid="button-next"
                className="px-6"
              >
                Next
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Step 2a: Select Kubernetes verbs for custom role
  if (step === "verbs") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <DialogTitle className="text-xl font-semibold">Select Verbs</DialogTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Choose Kubernetes verbs for <span className="font-medium text-foreground">{resourceName}</span>
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            {/* Search Section */}
            <Card>
              <CardContent className="pt-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search verbs by name or description..."
                    value={verbSearch}
                    onChange={(e) => setVerbSearch(e.target.value)}
                    className="pl-10 text-sm"
                    data-testid="input-verb-search"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Kubernetes Verbs ({selectedVerbs.length} selected)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {filteredVerbs.map((verb) => {
                    const isSelected = selectedVerbs.includes(verb.id);
                    return (
                      <div
                        key={verb.id}
                        className={`p-3 border rounded-lg cursor-pointer hover-elevate transition-all ${isSelected ? "border-purple-600 bg-purple-50 dark:bg-purple-950/20" : "border-muted"
                          }`}
                        onClick={() => toggleVerb(verb.id)}
                        data-testid={`verb-${verb.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected ? "border-purple-600 bg-purple-600" : "border-muted-foreground"
                            }`}>
                            {isSelected && (
                              <CheckCircle2 className="w-3 h-3 text-white" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm">{verb.name}</div>
                            <div className="text-xs text-muted-foreground">{verb.description}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {filteredVerbs.length === 0 && (
                  <div className="text-center py-8">
                    <Search className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">
                      No verbs found matching your search.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Try adjusting your search criteria.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex justify-between gap-3 pt-4">
              <Button variant="outline" onClick={handleBack} data-testid="button-back" className="px-6">
                Back
              </Button>
              <Button
                onClick={() => setStep("request")}
                disabled={selectedVerbs.length === 0}
                data-testid="button-next"
                className="px-6"
              >
                Next
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Step 2b: Show role selection step (for existing roles)
  if (step === "roles") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                {resourceType === "gcp" ? (
                  <Cloud className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                ) : (
                  <Database className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                )}
              </div>
              <div>
                <DialogTitle className="text-xl font-semibold">Select Role</DialogTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Choose a role to request access to <span className="font-medium text-foreground">{resourceName}</span>
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            {/* Search Section */}
            <Card>
              <CardContent className="pt-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search roles by name or description..."
                    value={roleSearch}
                    onChange={(e) => setRoleSearch(e.target.value)}
                    className="pl-10 text-sm"
                    data-testid="input-role-search"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Roles List */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Available Roles ({filteredRoles?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {filteredRoles?.map((role) => {
                    // Prepare role details for DetailsDialog
                    const roleDetails: any = {
                      name: role.name,
                      description: role.description || "No description",
                    };

                    if (resourceType === "kubernetes") {
                      roleDetails.verbs = role.verbs || [];
                      roleDetails.resources = role.resources || [];
                      roleDetails.apiGroups = role.apiGroups || [];
                    } else if (resourceType === "gcp") {
                      roleDetails.permissions = role.permissions || [];
                    } else if (resourceType === "aws") {
                      roleDetails.permissions = role.permissions || [];
                    } else if (resourceType === "azure") {
                      roleDetails.permissions = role.permissions || [];
                    }
                    const isSelected = selectedRoles.some((r) => r.id === role.id);
                    return (
                      <div
                        key={role.id}
                        className={`p-4 border-2 rounded-lg hover-elevate transition-all duration-200 group cursor-pointer ${isSelected ? "border-blue-600 bg-blue-50 dark:bg-blue-950/20" : "border-muted"
                          }`}
                        onClick={() => toggleRoleSelect(role)}
                        data-testid={`role-${role.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1">
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? "border-blue-600 bg-blue-600" : "border-muted-foreground"
                              }`}>
                              {isSelected && (
                                <CheckCircle2 className="w-3 h-3 text-white" />
                              )}
                            </div>
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 flex items-center justify-center group-hover:scale-105 transition-transform flex-shrink-0">
                              <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm max-w-full break-words break-all whitespace-normal">
                                {role.name}
                              </div>
                              {role.description && (
                                <div className="text-xs text-muted-foreground break-words break-all whitespace-normal">
                                  {role.description}
                                </div>
                              )}
                            </div>


                          </div>
                          <div onClick={(e) => e.stopPropagation()}>
                            <DetailsDialog
                              data={role}
                              title={`${role.name} Role Details`}
                              trigger="icon"
                              testId={`button-role-info-${role.id}`}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {filteredRoles?.length === 0 && (
                  <div className="text-center py-8">
                    <Search className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">
                      No roles found matching your search.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Try adjusting your search criteria.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex justify-between gap-3 pt-4">
              {resourceType === "kubernetes" ? (
                <Button variant="outline" onClick={handleBack} data-testid="button-back" className="px-6">
                  Back
                </Button>
              ) : (
                <Button variant="outline" onClick={handleCancel} data-testid="button-cancel" className="px-6">
                  Cancel
                </Button>
              )}
              <div />
              <Button
                onClick={() => setStep("request")}
                disabled={selectedRoles.length === 0}
                data-testid="button-next"
                className="px-6"
              >
                Next
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Main request form
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/20 dark:to-blue-800/30 flex items-center justify-center">
              {resourceType === "gcp" ? (
                <Cloud className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              ) : (
                <Database className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              )}
            </div>
            <div>
              <DialogTitle className="text-xl font-semibold">
                Request {resourceType === "gcp" ? "GCP" : resourceType === "aws" ? "AWS" : resourceType === "azure" ? "Azure" : "Kubernetes"} Access
              </DialogTitle>
              <p className="text-sm text-muted-foreground">
                Configure access for <span className="font-medium text-foreground">{resourceName}</span>
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Access Type */}
          <div>
            <Label className="text-sm font-medium mb-3 block flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Access Type
            </Label>
            <RadioGroup value={accessType} onValueChange={setAccessType} className="grid grid-cols-2 gap-3">
              <div className="relative">
                <RadioGroupItem value="DYNAMIC" id="DYNAMIC" className="peer sr-only" />
                <Label
                  htmlFor="DYNAMIC"
                  className="flex items-center justify-center rounded-lg border-2 border-muted bg-popover p-3 hover-elevate cursor-pointer peer-data-[state=checked]:border-blue-600 peer-data-[state=checked]:bg-blue-50 dark:peer-data-[state=checked]:bg-blue-950/20 transition-all"
                >
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                    <span className="font-medium text-sm">Timed</span>
                  </div>
                </Label>
              </div>
              <div className="relative">
                <RadioGroupItem value="permanent" id="permanent" className="peer sr-only" />
                <Label
                  htmlFor="permanent"
                  className="flex items-center justify-center rounded-lg border-2 border-muted bg-popover p-3 hover-elevate cursor-pointer peer-data-[state=checked]:border-blue-600 peer-data-[state=checked]:bg-blue-50 dark:peer-data-[state=checked]:bg-blue-950/20 transition-all"
                >
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <span className="font-medium text-sm">Permanent</span>
                  </div>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Request Details */}
          <Card>
            <CardContent className="pt-3 space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-xs text-muted-foreground">Resource</Label>
                  <div className="font-medium">{resourceName}</div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Type</Label>
                  <div className="font-medium">{resourceTypeLabel || (resourceType === "gcp" ? "GCP" : "Kubernetes")}</div>
                </div>
              </div>

              {/* Selected role display */}
              {selectedRoles.length > 0 && (
                <div className="pt-2 border-t">
                  <div className="flex items-center gap-2 pt-2 border-t">
                    <Shield className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <div className="text-sm">
                      <span className="text-muted-foreground"> Selected Role{selectedRoles.length > 1 ? 's' : ''}: </span>
                      <span className="font-medium">
                        {rolesToUse
                          ?.filter(r => selectedRoles.some(sr => sr.id === r.id))
                          .map(r =>
                            <Badge key={r.id} variant="secondary" className="text-xs border-foreground">
                              {r?.name}
                            </Badge>)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Custom role verbs display */}
              {roleMode === "custom" && selectedVerbs.length > 0 && (
                <div className="pt-2 border-t">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    <span className="text-xs text-muted-foreground">Custom Role Verbs:</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedVerbs.map(verbId => {
                      const verb = k8sVerbs.find(v => v.id === verbId);
                      return (
                        <Badge key={verbId} variant="secondary" className="text-xs">
                          {verb?.name}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Schedule Access  
          TODO: Backend need to handle for all cloud with Kubernetes
          <div className="flex items-center justify-between">
            <Label htmlFor="schedule-access" className="text-sm font-medium cursor-pointer">Schedule Access</Label>
            <Switch
              id="schedule-access"
              checked={scheduleAccess}
              onCheckedChange={setScheduleAccess}
            />
          </div>
*/}
          {/* Justification and Configuration */}
          <Card>
            <CardContent className="pt-3 space-y-3">
              {/* Justification */}
              <div>
                <Label htmlFor="justification" className="text-sm font-medium mb-2 block flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Access Justification *
                </Label>
                <Textarea
                  id="justification"
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  placeholder="Provide business justification for this access request..."
                  className="min-h-[80px] resize-none"
                  data-testid="textarea-justification"
                />
              </div>

              {/* Time Input */}
              {accessType === "DYNAMIC" && (
                <div>
                  <Label htmlFor="time-minutes" className="text-sm font-medium mb-2 block flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Duration (minutes) *
                  </Label>
                  <Input
                    id="time-minutes"
                    value={timeMinutes}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "") {
                        setTimeMinutes("");
                        return;
                      }
                      if (!/^\d+$/.test(value)) return;
                      const num = Number(value);
                      if (num >= 1 && num <= 999) {
                        setTimeMinutes(value);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (["e", "E", "+", "-", "."].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    placeholder="e.g., 60"
                    type="number"
                    min="1"
                    max="999"
                    className="max-w-xs"
                    data-testid="input-time-minutes"
                  />
                </div>
              )}

              {/* Schedule Access Date Fields */}
              {scheduleAccess && (
                <div>
                  <Label className="text-sm font-medium mb-2 block flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Schedule Window *
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="start-date" className="text-xs text-muted-foreground mb-1 block">
                        Start
                      </Label>
                      <Input
                        id="start-date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        type="datetime-local"
                        className="dark:[color-scheme:dark] dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"
                        data-testid="input-start-date"
                      />
                    </div>
                    <div>
                      <Label htmlFor="end-date" className="text-xs text-muted-foreground mb-1 block">
                        End
                      </Label>
                      <Input
                        id="end-date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        type="datetime-local"
                        className="dark:[color-scheme:dark] dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"
                        data-testid="input-end-date"
                      />
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-between gap-3 pt-3 border-t">
            <Button variant="outline" onClick={handleBack} data-testid="button-back">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex gap-3">
              <Button variant="outline" onClick={handleCancel} data-testid="button-cancel">
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={
                  !justification.trim() ||
                  (accessType === "DYNAMIC" && !timeMinutes) ||
                  (scheduleAccess && (!startDate || !endDate))
                }
                data-testid="button-submit"
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
              >
                <Shield className="w-4 h-4 mr-2" />
                Submit
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
